import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from 'components/Typography';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import CreditCardNumber, { validate as validateNumber } from 'components/forms/CreditCardForm/CreditCardNumber';
import CreditCardExpiration, { validate as validateExpiration } from 'components/forms/CreditCardForm/CreditCardExpiration';
import cvvIcon from '../../../../assets/images/pro/cvv_icon.png';
import OutlinedTextField from '../textFields/OutlinedTextField';
import * as styles from './CreditCardForm.module.scss';

const CardValidator = require('card-validator');

type Props = {
  changeHandler?(...args: unknown[]): unknown;
  cvv?: string;
  errors?: {
    cvv?: ReactNode;
    expiration?: ReactNode;
    name?: ReactNode;
    number?: ReactNode;
    postalCode?: ReactNode;
  };
  expiration?: string;
  isCouponInputExpanded?: boolean;
  isCouponInputShown?: boolean;
  name?: string;
  number?: string;
  onSubmit?(...args: unknown[]): void;
  postalCode?: string;
  setError: (name: 'expiration' | 'number', error: ReactNode) => void;
  toggleCouponCode?: () => void;
};

export function validate({
  cvv,
  name,
  postalCode,
  setCvvError,
  setNameError,
  setPostalCodeError,
  setNumberError,
  setExpirationError,
  number,
  expiration
}: {
  cvv: string;
  name: string;
  postalCode: string;
  number: string;
  expiration: string;
  setCvvError: (error: ReactNode) => void;
  setNameError: (error: ReactNode) => void;
  setPostalCodeError: (error: ReactNode) => void;
  setNumberError: (error: ReactNode) => void;
  setExpirationError: (error: ReactNode) => void;
}) {
  // I think we can clean these up into separate "validator" methods down the road
  const cvvErrorMessage = validatePaymentForm('cvv', cvv, {}).errors.cvv;
  const nameErrorMessage = validatePaymentForm('name', name, {}).errors.name;
  const postalCodeErrorMessage = validatePaymentForm('postalCode', postalCode, {}).errors.postalCode;

  const numberErrorMessage = validateNumber(number);
  const expirationErrorMessage = validateExpiration(expiration);

  setCvvError(cvvErrorMessage);
  setNameError(nameErrorMessage);
  setPostalCodeError(postalCodeErrorMessage);
  setNumberError(numberErrorMessage);
  setExpirationError(expirationErrorMessage);

  return cvvErrorMessage || nameErrorMessage || postalCodeErrorMessage || expirationErrorMessage || numberErrorMessage;
}

function CreditCardForm({
  errors,
  changeHandler,
  onSubmit,
  name,
  number,
  cvv,
  postalCode,
  setError,
  expiration,
  toggleCouponCode,
  isCouponInputExpanded = false,
  isCouponInputShown = false
}: Props) {
  const cardInfo = CardValidator.number(number);

  const getFormattedCVVCode = (cvvCode: string) => {
    const response = [];
    let maxLength = 3;

    if (cvvCode) {
      const cvvArray = cvvCode.split('');
      if (cardInfo.card && cardInfo.card.code) {
        maxLength = cardInfo.card.code.size;
      }
      for (let i = 0; i < cvvArray.length; i++) {
        if (i < maxLength) {
          response.push(cvvCode[i].replace(/\D/g, ''));
        }
      }
    }
    return response.join('');
  };

  // Design: https://www.figma.com/file/vJ30CAAm8qdbDO0j5C4Ce7/Web-Pro-Purchase-Flow?node-id=382%3A39640

  return (
    <div>
      <Typography component="div" mb="8">
        <CreditCardNumber
          changeHandler={changeHandler}
          error={errors.number}
          number={number}
          setError={error => setError('number', error)}
          submitHandler={onSubmit}
        />
      </Typography>
      <div className={styles.exp}>
        <CreditCardExpiration
          enterKeyHandler={onSubmit}
          error={errors.expiration}
          onChange={changeHandler}
          setError={error => setError('expiration', error)}
          value={expiration}
        />
        <OutlinedTextField
          adornment={<img alt="cvv code" className={styles.cardIcon} src={cvvIcon} />}
          autocomplete="cc-csc"
          changeHandler={changeHandler}
          enterKeyHandler={onSubmit}
          errorMessage={errors.cvv}
          inputMode="numeric"
          label={<FormattedMessage defaultMessage="CVV" />}
          name="cvv"
          value={getFormattedCVVCode(cvv)}
        />
      </div>
      <Typography component="div" mb="24">
        <OutlinedTextField
          autocomplete="cc-name"
          changeHandler={changeHandler}
          enterKeyHandler={onSubmit}
          errorMessage={errors.name}
          label={<FormattedMessage defaultMessage="Name on card" />}
          name="name"
          value={name}
        />
      </Typography>
      <div className={styles.postal}>
        <OutlinedTextField
          autocomplete="postal-code"
          changeHandler={changeHandler}
          enterKeyHandler={onSubmit}
          errorMessage={errors.postalCode}
          label={<FormattedMessage defaultMessage="Zip code" />}
          name="postalCode"
          value={postalCode}
        />
        {isCouponInputShown && (
          <div className={styles.couponCol}>
            <button className={styles.couponButton} onClick={toggleCouponCode} type="button">
              {isCouponInputExpanded ? (
                <FormattedMessage defaultMessage="Hide coupon code" />
              ) : (
                <FormattedMessage defaultMessage="Enter a coupon code" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreditCardForm;
